<template>
  <div class="py-4 container-fluid position-relative" id="dashboard-capture-area">

    <!-- ── 1. KPI Row: Tokens/Counters (Manager/Admin/Superadmin) OR Branches/Devices (Operator) ── -->
    <div v-if="['tokenmanager', 'admin', 'superadmin', 'operator', 'opertor', 'tokenuser'].includes(userRole)" class="row mb-4">
      <!-- Tokens Today / Total Branches -->
      <div class="col-xl-4 col-md-6 mb-4">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-icon bg-soft-info">
              <i :class="(userRole === 'operator' || userRole === 'opertor') ? 'ni ni-world text-info' : 'ni ni-tag text-info'"></i>
            </span>
            <div>
              <p class="kpi-label">{{ (userRole === 'operator' || userRole === 'opertor') ? 'Total Branches' : 'Tokens Today' }}</p>
              <h4 class="kpi-value">{{ (userRole === 'operator' || userRole === 'opertor') ? totalBranches : totalTokensToday }}</h4>
            </div>
          </div>
        </div>
      </div>
      <!-- Active Counters / Total Devices -->
      <div class="col-xl-4 col-md-6 mb-4">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-icon bg-soft-success">
              <i :class="(userRole === 'operator' || userRole === 'opertor') ? 'ni ni-tablet-button text-success' : 'ni ni-shop text-success'"></i>
            </span>
            <div>
              <p class="kpi-label">{{ userRole === 'tokenuser' ? 'Assigned Counters' : ((userRole === 'operator' || userRole === 'opertor') ? 'Total Devices' : 'Active Counters') }}</p>
              <h4 class="kpi-value">{{ userRole === 'tokenuser' ? assignedCounters.length : ((userRole === 'operator' || userRole === 'opertor') ? totalDevices : totalCounters) }}</h4>
            </div>
          </div>

          <!-- Counter Names List for Token User -->
          <div v-if="userRole === 'tokenuser'" class="mt-2 pt-2 border-top">
            <small class="text-muted d-block mb-1 font-weight-bold" style="font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.03em;">Assigned Counter Names:</small>
            <div v-if="assignedCounters.length > 0" class="d-flex flex-wrap gap-1">
              <span v-for="c in assignedCounters" :key="c.id" class="badge bg-soft-success text-success px-2 py-1" style="font-size: 0.75rem;">
                <i class="ni ni-shop me-1"></i>{{ c.name }}
              </span>
            </div>
            <span v-else class="text-muted text-xs italic">No counter currently assigned</span>
          </div>
        </div>
      </div>

      <!-- ── NEW KPI CARD: ATTENDED BY ME (AMIT) ── -->
      <div class="col-xl-4 col-md-6 mb-4">
        <div class="kpi-card">
          <div class="kpi-header">
            <span class="kpi-icon bg-soft-primary">
              <i class="ni ni-badge text-primary"></i>
            </span>
            <div>
              <p class="kpi-label">My Completed Tokens</p>
              <h4 class="kpi-value">{{ myCompletedTokensCount }}</h4>
            </div>
          </div>
          <div class="mt-1">
            <small class="text-muted text-xxs font-weight-bold" style="text-transform: uppercase;">Logged in as: {{ currentLoggedUserName || 'User' }}</small>
          </div>
        </div>
      </div>
    </div>

    <!-- ── 2. SECTION: BRANCH BROADCAST & CYCLE AUDIT (MOVED TO CHILD COMPONENT) ── -->
    <BranchAuditSection 
      v-if="['admin', 'superadmin', 'mediamanager', 'operator', 'opertor'].includes(userRole)"
      :userRole="userRole" 
      :apiUrl="apiUrl" 
    />

    <!-- ── CHARTS CONTAINER ── -->
    <div v-if="chartsReady">
      
      <!-- ── TOP CHARTS ROW: Users, Devices, Playlist, Media, Emergency Broadcast ── -->
      <div class="row mb-4">

        <!-- 1. USER ANALYSIS DIAGRAM -->
        <div v-if="!['operator', 'opertor', 'tokenmanager', 'tokenuser'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header">
              <span class="kpi-icon bg-soft-info"><i class="ni ni-single-02 text-info"></i></span>
              <div>
                <p class="kpi-label">User Management</p>
                <h4 class="kpi-value">Account Status Overview</h4>
              </div>
            </div>
            <div class="chart-box-bar-wide">
              <canvas ref="userStatusBarChart"></canvas>
            </div>
          </div>
        </div>

        <!-- 2. DEVICE ANALYSIS DIAGRAM -->
        <div v-if="userRole !== 'tokenmanager' && userRole !== 'tokenuser'" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon" :style="{ backgroundColor: themeColor + '1a' }">
                <i class="ni ni-tablet-button" :style="{ color: themeColor }"></i>
              </span>
              <div>
                <p class="kpi-label">Device Analysis</p>
                <h4 class="kpi-value">{{ (userRole === 'supervisor' || userRole === 'superviser') ? 'Devices by State' : 'By Location' }}</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="deviceLocationDoughnut"></canvas>
            </div>
          </div>
        </div>

        <!-- 3. PLAYLIST STATUS DIAGRAM -->
        <div v-if="(userRole === 'admin' || userRole === 'superadmin' || userRole === 'mediamanager' || userRole === 'supervisor' || userRole === 'superviser' || userRole === 'operator' || userRole === 'opertor') && !['tokenuser', 'tokenmanager'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon bg-soft-secondary"><i class="ni ni-bullet-list-67 text-secondary"></i></span>
              <div>
                <p class="kpi-label">{{ (userRole === 'operator' || userRole === 'opertor') ? 'Branch Analysis' : 'Geographic Overview' }}</p>
                <h4 class="kpi-value">{{ (userRole === 'supervisor' || userRole === 'superviser') ? 'Branches by City' : ((userRole === 'operator' || userRole === 'opertor') ? 'Branch Status' : 'Playlist Status') }}</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="playlistStatusBarChart"></canvas>
            </div>
          </div>
        </div>

        <!-- 4. MEDIA INVENTORY DIAGRAM -->
        <div v-if="!['operator', 'opertor', 'tokenmanager', 'tokenuser'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon" :style="{ backgroundColor: themeColor + '1a' }">
                <i class="ni ni-album-2" :style="{ color: themeColor }"></i>
              </span>
              <div>
                <p class="kpi-label">Inventory Breakdown</p>
                <h4 class="kpi-value">{{ (userRole === 'supervisor' || userRole === 'superviser') ? 'Devices per Branch' : 'Media Status & Type' }}</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="mediaStatusBarChart"></canvas>
            </div>
          </div>
        </div>

        <!-- 5. EMERGENCY BROADCAST DIAGRAM -->
        <div v-if="!['operator', 'opertor', 'tokenmanager', 'tokenuser'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header">
              <span class="kpi-icon bg-soft-danger"><i class="ni ni-notification-70 text-danger"></i></span>
              <div>
                <p class="kpi-label">Emergency Alerts</p>
                <h4 class="kpi-value">Broadcast Template Analysis</h4>
              </div>
            </div>
            <div class="chart-box-bar-wide">
              <canvas ref="emergencyStatusBar"></canvas>
            </div>
          </div>
        </div>

      </div>

      <!-- ── 6. TOKEN MANAGER / ADMIN / SUPERADMIN COMPACT STARTING CARD VIEW ── -->
      <div v-if="['tokenmanager', 'admin', 'superadmin'].includes(userRole) && !isCounterChartExpanded" class="row mb-4">
        <div class="col-xl-12 col-md-12 mb-4">
          <div class="card shadow-sm border-0 p-3" style="border-radius: 1rem;">
            <div class="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
              <h6 class="font-weight-bolder mb-0" style="color: #1e3a5f; font-size: 0.95rem;">
                {{ (userRole === 'tokenmanager') ? 'Counters Breakdown' : (selectedBranchDrilldown ? ('Counters Breakdown (' + (selectedBranchDrilldown.branch_name || selectedBranchDrilldown.name) + ')') : 'Branches Breakdown') }}
              </h6>
              <!-- Header Badges, Searchable Dropdown, and Circle Action Buttons -->
              <div class="d-flex align-items-center gap-2 flex-wrap">
                
                <!-- SEARCHABLE COUNTER DROPDOWN FOR TOKEN MANAGER WHEN > 5 COUNTERS -->
                <div v-if="userRole === 'tokenmanager' && countersList.length > 5" class="position-relative me-1" style="min-width: 200px;">
                  <div class="input-group input-group-sm">
                    <span class="input-group-text bg-white border-end-0 py-1 pe-1">
                      <i class="ni ni-zoom-split-in text-xs text-secondary"></i>
                    </span>
                    <input 
                      type="text" 
                      class="form-control form-control-sm border-start-0 ps-1 py-1 text-xs" 
                      placeholder="Search counter..." 
                      v-model="counterSearchQuery"
                      @focus="showCounterDropdown = true"
                      @input="showCounterDropdown = true"
                      style="border-radius: 0 6px 6px 0;"
                    />
                  </div>

                  <!-- FLOATING SEARCHABLE DROPDOWN MENU -->
                  <div v-if="showCounterDropdown" class="search-dropdown-menu shadow-lg border rounded-3 bg-white w-100 position-absolute" style="top: 100%; left: 0; z-index: 1050; max-height: 220px; overflow-y: auto; margin-top: 4px;">
                    <div 
                      class="p-2 border-bottom text-xs font-weight-bold text-primary cursor-pointer d-flex justify-content-between align-items-center"
                      style="background-color: #f8fafc;"
                      @click="selectCounterFromDropdown(null)"
                    >
                      <span>All Counters ({{ countersList.length }})</span>
                      <span class="badge bg-secondary text-xxs">{{ totalTokensToday }} Tokens</span>
                    </div>
                    <div 
                      v-for="c in filteredDropdownCounters" 
                      :key="c.id" 
                      class="p-2 border-bottom text-xs cursor-pointer d-flex justify-content-between align-items-center dropdown-counter-item"
                      @click="selectCounterFromDropdown(c)"
                    >
                      <span class="text-dark font-weight-bold">{{ c.name }}</span>
                      <span class="badge bg-soft-info text-info text-xxs px-2 py-1 font-weight-bold" style="border-radius: 4px;">
                        {{ getCounterAttendedTokens(c).length }} Tokens
                      </span>
                    </div>
                    <div v-if="filteredDropdownCounters.length === 0" class="p-2 text-center text-xs text-muted italic">
                      No matching counters found
                    </div>
                  </div>
                </div>

                <span v-if="selectedCounterDrilldown && userRole === 'tokenmanager'" class="badge bg-soft-primary text-primary px-3 py-2 font-weight-bold" style="font-size: 0.75rem;">
                  {{ selectedCounterDrilldown.name }}: {{ getCounterAttendedTokens(selectedCounterDrilldown).length }} Tokens
                </span>

                <span class="badge bg-gradient-success text-white px-3 py-2 font-weight-bold shadow-sm" style="font-size: 0.75rem;">
                  <i class="ni ni-check-bold me-1"></i> {{ tokenStatusStats.completed }} Total Completed Tokens
                </span>
                <span class="badge bg-soft-info text-info px-3 py-2 font-weight-bold" style="font-size: 0.75rem;">
                  <i class="ni ni-shop me-1"></i> {{ (userRole === 'tokenmanager') ? countersList.length + ' Counters' : (selectedBranchDrilldown ? getBranchCounters(selectedBranchDrilldown).length + ' Counters' : branchesList.length + ' Branches') }}
                </span>
                <button v-if="userRole === 'tokenmanager' ? !!selectedCounterDrilldown : (!!selectedCounterDrilldown || !!selectedBranchDrilldown)" class="btn btn-outline-secondary btn-sm mb-0 px-2 py-1 font-weight-bold" style="border-radius: 6px; font-size: 0.7rem;" @click="handleBackDrilldown">
                  &lt; BACK
                </button>

                <!-- DOWNLOAD DROPDOWN BUTTON -->
                <div class="dropdown d-inline-block position-relative">
                  <button 
                    class="btn btn-sm btn-dark mb-0 px-3 py-1 font-weight-bold d-flex align-items-center" 
                    id="downloadDropdown"
                    type="button" 
                    @click="showDownloadMenu = !showDownloadMenu"
                    style="border-radius: 6px; font-size: 0.75rem;"
                  >
                    <i class="ni ni-cloud-download-95 me-1"></i> DOWNLOAD
                  </button>
                  <ul v-if="showDownloadMenu" class="dropdown-menu show shadow border-0 py-2" 
                      style="position: absolute; right: 0; top: 100%; z-index: 1100; min-width: 160px; display: block;">
                    <li>
                      <a class="dropdown-item text-xs font-weight-bold" href="#" @click.prevent="runDownload('pdf')">
                        <i class="ni ni-single-copy-04 text-danger me-2"></i> Save as PDF (Report)
                      </a>
                    </li>
                    <li>
                      <a class="dropdown-item text-xs font-weight-bold" href="#" @click.prevent="runDownload('excel')">
                        <i class="ni ni-calendar-grid-58 text-success me-2"></i> Save as Excel (.csv)
                      </a>
                    </li>
                  </ul>
                </div>

                <button class="circle-btn" title="Info">
                  <i class="ni ni-info text-secondary"></i>
                </button>
                <button class="circle-btn" @click="toggleExpandCounterChart" title="Expand View">
                  <i class="ni ni-bold-up text-dark"></i>
                </button>
              </div>
            </div>
            <div class="chart-box-pie position-relative" style="height: 280px;">
              <canvas ref="counterStatusChart" :key="'compact-' + (selectedBranchDrilldown ? selectedBranchDrilldown.id : 'base')"></canvas>
            </div>
          </div>
        </div>
      </div>

      <!-- ── 6B. EXPANDED / DRILL-DOWN VIEW FOR TOKEN MANAGER / ADMIN / SUPERADMIN ── -->
      <div v-if="['tokenmanager', 'admin', 'superadmin'].includes(userRole) && isCounterChartExpanded" class="card shadow-sm border-0 p-4 mb-4" style="border-radius: 0.5rem; min-height: 520px; background: #fff;">
        
        <div class="d-flex justify-content-between align-items-center pb-3 mb-3 border-bottom flex-wrap gap-2">
          <div class="d-flex align-items-center gap-2">
            <span v-if="selectedBranchDrilldown && userRole !== 'tokenmanager'" style="color: #1e3a5f; font-weight: 600; font-size: 0.9rem;">
              <i class="ni ni-bold-right me-1"></i>{{ selectedBranchDrilldown.branch_name || selectedBranchDrilldown.name }}
            </span>
            <span v-if="selectedCounterDrilldown" style="color: #d63384; font-weight: 600; font-size: 0.9rem;">
              <i class="ni ni-bold-right me-1"></i>{{ selectedCounterDrilldown.name }}
            </span>
          </div>

          <div class="d-flex align-items-center gap-2 flex-wrap">
            
            <div v-if="userRole === 'tokenmanager' && countersList.length > 5" class="position-relative me-1" style="min-width: 200px;">
              <div class="input-group input-group-sm">
                <span class="input-group-text bg-white border-end-0 py-1 pe-1">
                  <i class="ni ni-zoom-split-in text-xs text-secondary"></i>
                </span>
                <input 
                  type="text" 
                  class="form-control form-control-sm border-start-0 ps-1 py-1 text-xs" 
                  placeholder="Search counter..." 
                  v-model="counterSearchQuery"
                  @focus="showCounterDropdown = true"
                  @input="showCounterDropdown = true"
                  style="border-radius: 0 6px 6px 0;"
                />
              </div>

              <div v-if="showCounterDropdown" class="search-dropdown-menu shadow-lg border rounded-3 bg-white w-100 position-absolute" style="top: 100%; left: 0; z-index: 1050; max-height: 220px; overflow-y: auto; margin-top: 4px;">
                <div 
                  class="p-2 border-bottom text-xs font-weight-bold text-primary cursor-pointer d-flex justify-content-between align-items-center"
                  style="background-color: #f8fafc;"
                  @click="selectCounterFromDropdown(null)"
                >
                  <span>All Counters ({{ countersList.length }})</span>
                  <span class="badge bg-secondary text-xxs">{{ totalTokensToday }} Tokens</span>
                </div>
                <div 
                  v-for="c in filteredDropdownCounters" 
                  :key="c.id" 
                  class="p-2 border-bottom text-xs cursor-pointer d-flex justify-content-between align-items-center dropdown-counter-item"
                  @click="selectCounterFromDropdown(c)"
                >
                  <span class="text-dark font-weight-bold">{{ c.name }}</span>
                  <span class="badge bg-soft-info text-info text-xxs px-2 py-1 font-weight-bold" style="border-radius: 4px;">
                    {{ getCounterAttendedTokens(c).length }} Tokens
                  </span>
                </div>
                <div v-if="filteredDropdownCounters.length === 0" class="p-2 text-center text-xs text-muted italic">
                  No matching counters found
                </div>
              </div>
            </div>

            <span class="badge bg-gradient-success text-white px-3 py-2 font-weight-bold shadow-sm" style="font-size: 0.75rem;">
              <i class="ni ni-check-bold me-1"></i> {{ tokenStatusStats.completed }} Total Completed Tokens
            </span>
            <span class="badge bg-soft-info text-info px-3 py-2 font-weight-bold" style="font-size: 0.75rem;">
              <i class="ni ni-shop me-1"></i> {{ (userRole === 'tokenmanager') ? countersList.length + ' Counters' : (selectedBranchDrilldown ? getBranchCounters(selectedBranchDrilldown).length + ' Counters' : branchesList.length + ' Branches') }}
            </span>
            <button v-if="userRole === 'tokenmanager' ? !!selectedCounterDrilldown : (!!selectedCounterDrilldown || !!selectedBranchDrilldown)" class="btn btn-outline-secondary btn-sm mb-0 px-3 py-1 font-weight-bold" style="border-radius: 6px; font-size: 0.75rem;" @click="handleBackDrilldown">
              &lt; BACK
            </button>
            <button class="circle-btn" @click="toggleExpandCounterChart" title="Shrink View">
              <i class="ni ni-bold-up"></i>
            </button>
          </div>
        </div>

        <div class="row flex-grow-1 align-items-center position-relative">
          <div :class="selectedCounterDrilldown ? 'col-lg-5 col-md-12' : 'col-12 d-flex justify-content-center'">
            <div class="chart-box-pie w-100" style="height: 380px;">
              <canvas ref="counterStatusChartExpanded" :key="'expanded-' + (selectedCounterDrilldown ? selectedCounterDrilldown.id : 'view')"></canvas>
            </div>
          </div>

          <div v-if="selectedCounterDrilldown" class="col-lg-7 col-md-12 border-start ps-4">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h6 class="font-weight-bolder mb-0" style="color: #2d3748; font-size: 0.95rem;">
                Records for: {{ selectedCounterDrilldown.name }}
              </h6>
              <span class="badge rounded-circle d-inline-flex align-items-center justify-content-center" style="width: 24px; height: 24px; background-color: #64748b; color: #fff; font-size: 0.75rem;">
                {{ getCounterAttendedTokens(selectedCounterDrilldown).length }}
              </span>
            </div>

            <div class="table-responsive border rounded" style="max-height: 360px; overflow-y: auto;">
              <table class="table align-items-center mb-0">
                <thead style="background-color: #f8fafc;">
                  <tr>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">SR. NO.</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">TOKEN #</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">CUSTOMER NAME</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">ATTENDED BY</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">SERVICE</th>
                    <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3">STATUS</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(tok, idx) in getCounterAttendedTokens(selectedCounterDrilldown)" :key="tok.id">
                    <td class="ps-3 text-xs text-secondary font-weight-bold">
                      {{ idx + 1 }}
                    </td>
                    <td class="ps-3">
                      <span class="badge px-2 py-1 text-white font-weight-bold" style="background-color: #1e3a8a; font-size: 0.7rem; border-radius: 4px;">
                        {{ tok.token_number }}
                      </span>
                    </td>
                    <td class="ps-3">
                      <span class="text-xs font-weight-bold text-dark">{{ tok.token_name || tok.name || 'N/A' }}</span>
                    </td>
                    <!-- NEW FIELD: ATTENDED BY -->
                    <td class="ps-3">
                      <span class="text-xs font-weight-bold text-primary">
                        {{ tok.token_attended && tok.token_attended !== 'no' ? tok.token_attended : (tok.user ? tok.user.name : 'Not Assigned') }}
                      </span>
                    </td>
                    <td class="ps-3">
                      <span class="text-xs text-secondary">{{ tok.service ? tok.service.name : (tok.service_name || 'N/A') }}</span>
                    </td>
                    <td class="ps-3">
                      <span v-if="tok.status === 'serving' || tok.status === 'processing'" class="badge px-2 py-1" style="background-color: #e0f2fe; color: #0284c7; font-size: 0.65rem; font-weight: 800; border-radius: 12px;">
                        PROCESSING
                      </span>
                      <span v-else-if="tok.status === 'completed'" class="badge px-2 py-1" style="background-color: #dcfce7; color: #16a34a; font-size: 0.65rem; font-weight: 800; border-radius: 12px;">
                        COMPLETED
                      </span>
                      <span v-else-if="tok.status === 'pending' || tok.status === 'waiting'" class="badge px-2 py-1" style="background-color: #fef9c3; color: #ca8a04; font-size: 0.65rem; font-weight: 800; border-radius: 12px;">
                        PENDING
                      </span>
                      <span v-else-if="tok.status === 'skipped'" class="badge px-2 py-1" style="background-color: #f3f4f6; color: #6b7280; font-size: 0.65rem; font-weight: 800; border-radius: 12px;">
                        SKIPPED
                      </span>
                      <span v-else-if="tok.status === 'rejected'" class="badge px-2 py-1" style="background-color: #fee2e2; color: #dc2626; font-size: 0.65rem; font-weight: 800; border-radius: 12px;">
                        REJECTED
                      </span>
                      <span v-else class="badge bg-soft-info text-info text-xxs px-2 py-1">{{ (tok.status || 'UNKNOWN').toUpperCase() }}</span>
                    </td>
                  </tr>

                  <tr v-if="getCounterAttendedTokens(selectedCounterDrilldown).length === 0">
                    <td colspan="6" class="text-center py-4 text-muted text-xs">
                      <i class="ni ni-archive-2 me-1"></i> No records found for this counter.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      <!-- ── 7. COUNTER ACTIVITY & TOKEN ALLOCATION TABLE (VISIBLE ONLY FOR TOKENUSER) ── -->
      <div v-if="userRole === 'tokenuser'" class="card shadow-sm border-0 p-4 mb-4" style="border-radius: 1rem;">
        
        <div class="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
          <div>
            <h6 class="font-weight-bolder mb-1" style="color: #1e3a5f; font-size: 1rem;">
              Counter Activity & Token Allocation
            </h6>
            <p class="text-muted text-xs mb-0">
              Live status of assigned active tokens and completed token performance per counter
            </p>
          </div>

          <div class="d-flex align-items-center gap-2">
            <span class="badge px-3 py-2 font-weight-bold shadow-sm" style="background-color: #1e293b; color: #fff; font-size: 0.75rem; border-radius: 6px;">
              <i class="ni ni-check-bold me-1"></i> {{ tokenStatusStats.completed }} TOTAL COMPLETED TOKENS
            </span>
            <span class="badge bg-soft-info text-info px-3 py-2 font-weight-bold" style="font-size: 0.75rem; border-radius: 6px;">
              <i class="ni ni-shop me-1"></i> {{ assignedCounters.length }} COUNTERS LISTED
            </span>
          </div>
        </div>

        <div class="table-responsive">
          <table class="table align-items-center mb-0">
            <thead>
              <tr style="border-bottom: 1px solid #f1f5f9;">
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3 py-3" style="letter-spacing: 0.05em;">BRANCH / COUNTER NAME</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3 py-3" style="letter-spacing: 0.05em;">CURRENTLY ASSIGNED TOKEN</th>
                <th class="text-uppercase text-secondary text-xxs font-weight-bolder opacity-7 ps-3 text-center" style="letter-spacing: 0.05em;">COMPLETED TOKENS</th>
              </tr>
            </thead>
            <tbody>
              <template v-for="c in assignedCounters.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage)" :key="c.id">
                <tr style="border-bottom: 1px solid #f8fafc;">
                  <td class="ps-3 py-3 align-middle">
                    <div class="d-flex align-items-center gap-3">
                      <span class="kpi-icon bg-soft-info" style="width: 32px; height: 32px; border-radius: 8px; flex-shrink: 0;">
                        <i class="ni ni-shop text-info" style="font-size: 0.85rem;"></i>
                      </span>
                      <div>
                        <div class="d-flex align-items-center gap-1 cursor-pointer" @click="toggleCounterExpand(c.id)">
                          <span class="font-weight-bold text-dark text-xs" style="color: #2d3748;">
                            {{ (c.branch ? (c.branch.branch_name || c.branch.name) : (c.branch_name || 'Main Branch')) }} / {{ c.name }}
                          </span>
                          <i :class="['ni', expandedCounterIds.includes(c.id) ? 'ni-chevron-up' : 'ni-chevron-down', 'text-xs', 'ms-1']" style="color: #d63384;"></i>
                        </div>
                        <div class="text-muted text-xxs d-flex align-items-center mt-1">
                          <i class="ni ni-single-02 me-1" style="font-size: 0.65rem;"></i>
                          <span>{{ c.user ? c.user.name : (c.attendant_name || 'Unassigned') }}</span>
                        </div>
                      </div>
                    </div>
                  </td>

                  <td class="ps-3 py-3 align-middle">
                    <div v-if="getCounterAssignedTokens(c).length > 0" class="d-flex flex-wrap gap-1">
                      <span v-for="tok in getCounterAssignedTokens(c)" :key="tok.id" class="badge bg-soft-info text-info px-2 py-1 font-weight-bold" style="font-size: 0.7rem; border-radius: 6px;">
                        <i class="ni ni-tag me-1"></i>{{ tok.token_number }} ({{ (tok.status || 'ACTIVE').toUpperCase() }})
                      </span>
                    </div>
                    <div v-else class="text-muted text-xs d-flex align-items-center">
                      <i class="ni ni-time-alarm me-1" style="font-size: 0.8rem;"></i>
                      <span style="font-style: italic; color: #64748b;">No active token / Idle</span>
                    </div>
                  </td>

                  <td class="ps-3 py-3 align-middle text-center">
                    <span class="font-weight-bold text-dark text-xs">
                      {{ getCounterCompletedTokens(c).length }}
                    </span>
                  </td>
                </tr>

                <tr v-if="expandedCounterIds.includes(c.id)" :key="'exp-' + c.id" style="background-color: #f8fafc;">
                  <td colspan="3" class="p-3">
                    <div class="border rounded p-3 bg-white shadow-xs">
                      <h6 class="text-xs font-weight-bold mb-2 text-dark">
                        Assigned Tokens Detail for {{ c.name }}
                      </h6>
                      <div v-if="getCounterAttendedTokens(c).length > 0" class="table-responsive">
                        <table class="table table-sm align-items-center mb-0">
                          <thead>
                            <tr>
                              <th class="text-xxs font-weight-bolder text-secondary ps-2">TOKEN #</th>
                              <th class="text-xxs font-weight-bolder text-secondary ps-2">CUSTOMER</th>
                              <th class="text-xxs font-weight-bolder text-secondary ps-2">SERVICE</th>
                              <th class="text-xxs font-weight-bolder text-secondary ps-2">STATUS</th>
                            </tr>
                          </thead>
                          <tbody>
                            <tr v-for="tok in getCounterAttendedTokens(c)" :key="'sub-' + tok.id">
                              <td class="ps-2 text-xs font-weight-bold">{{ tok.token_number }}</td>
                              <td class="ps-2 text-xs">{{ tok.token_name || tok.name || 'N/A' }}</td>
                              <td class="ps-2 text-xs text-secondary">{{ tok.service ? tok.service.name : (tok.service_name || 'N/A') }}</td>
                              <td class="ps-2">
                                <span class="badge bg-soft-primary text-primary px-2 py-1" style="font-size: 0.65rem;">
                                  {{ (tok.status || 'UNKNOWN').toUpperCase() }}
                                </span>
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div v-else class="text-muted text-xs italic py-2">
                        No token activity records found for this counter today.
                      </div>
                    </div>
                  </td>
                </tr>
              </template>

              <tr v-if="assignedCounters.length === 0">
                <td colspan="3" class="text-center py-4 text-muted text-xs">
                  <i class="ni ni-archive-2 me-1"></i> No counters available.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div class="d-flex justify-content-between align-items-center pt-3 mt-2 border-top flex-wrap gap-2">
          <div class="text-muted text-xs">
            Showing {{ (assignedCounters.length === 0) ? 0 : (currentPage - 1) * itemsPerPage + 1 }} to {{ Math.min(currentPage * itemsPerPage, assignedCounters.length) }} of {{ assignedCounters.length }} counters
          </div>

          <div class="d-flex align-items-center gap-1">
            <button 
              class="btn btn-outline-secondary btn-sm mb-0 px-3 py-1 text-xs" 
              style="border-radius: 6px; font-weight: 600;" 
              :disabled="currentPage === 1" 
              @click="currentPage--"
            >
              PREVIOUS
            </button>

            <button 
              v-for="p in totalPages" 
              :key="p" 
              :class="['btn', 'btn-sm', 'mb-0', 'px-3', 'py-1', 'text-xs', currentPage === p ? 'btn-primary' : 'btn-outline-secondary']" 
              style="border-radius: 6px; font-weight: 600;"
              @click="currentPage = p"
            >
              {{ p }}
            </button>

            <button 
              class="btn btn-outline-secondary btn-sm mb-0 px-3 py-1 text-xs" 
              style="border-radius: 6px; font-weight: 600;" 
              :disabled="currentPage === totalPages || totalPages === 0" 
              @click="currentPage++"
            >
              NEXT
            </button>
          </div>
        </div>

      </div>

      <!-- ── BOTTOM CHARTS ROW: Tokens, Counters, Services ── -->
      <div class="row mb-4">
        
        <div v-if="['tokenmanager', 'tokenuser', 'admin', 'superadmin', 'devicemanager'].includes(this.userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon bg-soft-danger"><i class="ni ni-notification-70 text-danger"></i></span>
              <div>
                <p class="kpi-label">Token Management</p>
                <h4 class="kpi-value">Status Breakdown</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="tokenStatusBar"></canvas>
            </div>
          </div>
        </div>

        <div v-if="!['tokenmanager', 'admin', 'superadmin'].includes(userRole) && ['tokenuser', 'devicemanager'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon bg-soft-success">
                <i class="ni ni-shop text-success"></i>
              </span>
              <div>
                <p class="kpi-label">Counter Management</p>
                <h4 class="kpi-value">Operational Status</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="counterStatusChart"></canvas>
            </div>
          </div>
        </div>

        <!-- ── NEW: STAFF PERFORMANCE CHART (TO SHOW AMIT'S DATA VS OTHERS) ── -->
        <div v-if="['admin', 'superadmin', 'tokenmanager'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon bg-soft-primary"><i class="ni ni-badge text-primary"></i></span>
              <div>
                <p class="kpi-label">Staff Performance</p>
                <h4 class="kpi-value">Tokens per Attendant</h4>
              </div>
            </div>
            <div class="chart-box-bar-wide">
              <canvas ref="staffPerformanceChart"></canvas>
            </div>
          </div>
        </div>

        <div v-if="['tokenmanager', 'admin', 'superadmin', 'tokenuser', 'devicemanager'].includes(userRole)" class="col-xl-6 col-md-12 mb-4">
          <div class="kpi-card">
            <div class="kpi-header justify-content-center">
              <span class="kpi-icon" :style="{ backgroundColor: themeColor + '1a' }">
                <i class="ni ni-bullet-list-67" :style="{ color: themeColor }"></i>
              </span>
              <div>
                <p class="kpi-label">Service Management</p>
                <h4 class="kpi-value">{{ userRole === 'tokenuser' ? 'Assigned Services' : 'Services Breakdown' }}</h4>
              </div>
            </div>
            <div class="chart-box-pie">
              <canvas ref="serviceStatusChart"></canvas>
            </div>
          </div>
        </div>

      </div>

    </div>

    <div v-else class="text-center text-muted py-5 mt-4">
      <div class="spinner-border text-primary" role="status"></div>
      <p class="mt-2">Building Data Visualizations...</p>
    </div>

  </div>
</template>

<script>
import axios from "axios";
import { Chart, registerables } from "chart.js";
import BranchAuditSection from "./BranchAuditSection.vue"; 
import jsPDF from "jspdf";

Chart.register(...registerables);

export default {
  name: "DashboardDefault",
  components: {
    BranchAuditSection
  },
  data() {
    return {
      apiUrl: (process.env.VUE_APP_API_BASE_URL) + "/v1/admin",
      userRole: (localStorage.getItem("userRole") || "").trim().toLowerCase(),
      userState: (localStorage.getItem("userState") || "").trim().toLowerCase(),
      userCity: (localStorage.getItem("userCity") || "").trim().toLowerCase(),
      themeColor: localStorage.getItem('themeColor') || '#1e3a5f',
      userId: localStorage.getItem("userId") || localStorage.getItem("id") || "",
      totalMedia: 0, totalUsers: 0, totalDevices: 0, totalBranches: 0, totalEmergency: 0, totalRoles: 0, totalPlaylists: 0,
      
      countersList: [],
      branchesList: [],
      selectedCounterForDetail: null,
      selectedCounterDrilldown: null, 
      selectedBranchDrilldown: null,  
      isCounterChartExpanded: false,  
      currentPage: 1,
      itemsPerPage: 5,
      totalCounters: 0,
      assignedCounters: [],
      totalTokensToday: 0,
      todayTokens: [],
      expandedCounterIds: [],

      counterSearchQuery: "",
      showCounterDropdown: false,
      showDownloadMenu: false,

      totalServices: 0,
      assignedServices: [],
      serviceStatusStats: { active: 0, inactive: 0 },

      mediaStatusStats: {
        active: { image: 0, video: 0 },
        inactive: { image: 0, video: 0 }
      },
      userStats: { active: 0, inactive: 0 },
      tokenStatusStats: { completed: 0, pending: 0, skipped: 0, rejected: 0 },
      playlistStatusStats: { active: 0, inactive: 0 },
      branchStatusStats: { active: 0, inactive: 0 },
      emergencyStats: { live: 0, active: 0, inactive: 0 },
      counterStatusStats: { active: 0, inactive: 0 },
      branchDistribution: [], 
      stateDeviceDist: [], 
      cityBranchDist: [],  
      chartsReady: false,
      charts: {},
    };
  },

  computed: {
    currentLoggedUserName() {
      return (localStorage.getItem("userName") || localStorage.getItem("name") || "").trim();
    },
    myCompletedTokensCount() {
      const name = this.currentLoggedUserName.toLowerCase();
      if (!name) return 0;
      return this.todayTokens.filter(t => 
        (t.status || '').toLowerCase() === 'completed' && 
        (t.token_attended || '').toLowerCase().trim() === name
      ).length;
    },
    totalPages() {
      const listLength = (this.userRole === 'tokenuser') ? this.assignedCounters.length : this.countersList.length;
      return Math.ceil(listLength / this.itemsPerPage) || 1;
    },
    paginatedCountersList() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      return this.countersList.slice(start, start + this.itemsPerPage);
    },
    filteredDropdownCounters() {
      if (!this.counterSearchQuery) return this.countersList;
      const q = this.counterSearchQuery.toLowerCase().trim();
      return this.countersList.filter(c => c.name && c.name.toLowerCase().includes(q));
    }
  },

  methods: {
    handleDocumentClick(e) {
      if (this.$el && !this.$el.querySelector('.search-dropdown-menu')?.contains(e.target) && !this.$el.querySelector('input[placeholder="Search counter..."]')?.contains(e.target)) {
        this.showCounterDropdown = false;
      }
      if (!e.target.closest('.dropdown')) {
        this.showDownloadMenu = false;
      }
    },

    // --- CONSOLIDATED DOWNLOAD ACTIONS ---
    runDownload(type) {
      this.showDownloadMenu = false; 
      if (type === 'pdf') {
        this.downloadAsPDF();
      } else if (type === 'excel') {
        this.downloadAsExcel();
      }
    },

async downloadAsPDF() {
  const btn = document.getElementById("downloadDropdown");
  const originalBtnText = btn.innerHTML;
  btn.innerHTML = '<span class="spinner-border spinner-border-sm me-2"></span> GENERATING DATA PDF...';

  try {
    // 1. Initialize PDF (A4 size, Portrait)
    const doc = new jsPDF("p", "mm", "a4");
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 15;
    let cursorY = 20;

    // --- REPORT HEADER ---
    doc.setFontSize(18);
    doc.setTextColor(30, 58, 95); // Theme Blue
    doc.text("Token Activity & Counter Report", margin, cursorY);
    
    cursorY += 8;
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated by: ${this.currentLoggedUserName || 'Admin'}`, margin, cursorY);
    doc.text(`Date: ${new Date().toLocaleDateString()} ${new Date().toLocaleTimeString()}`, pageWidth - margin, cursorY, { align: 'right' });
    
    cursorY += 5;
    doc.setDrawColor(30, 58, 95);
    doc.line(margin, cursorY, pageWidth - margin, cursorY); // Horizontal Line

    // --- SUMMARY SECTION ---
    cursorY += 12;
    doc.setFontSize(12);
    doc.setTextColor(0);
    doc.setFont(undefined, 'bold');
    doc.text("Daily Summary", margin, cursorY);
    
    cursorY += 8;
    doc.setFontSize(10);
    doc.setFont(undefined, 'normal');
    const summaryData = [
      `Total Tokens: ${this.totalTokensToday}`,
      `Completed: ${this.tokenStatusStats.completed}`,
      `Pending: ${this.tokenStatusStats.pending}`,
      `Skipped/Rejected: ${this.tokenStatusStats.skipped + this.tokenStatusStats.rejected}`
    ];
    doc.text(summaryData.join("   |   "), margin, cursorY);

    // --- DATA TABLE ---
    cursorY += 15;
    doc.setFont(undefined, 'bold');
    doc.setFillColor(240, 242, 245);
    doc.rect(margin, cursorY, pageWidth - (margin * 2), 8, 'F');
    
    // Updated Table Headers and Column Widths to include Counter Name
    const colWidths = { sr: 10, token: 20, counter: 35, name: 30, attended: 30, service: 35, status: 20 };
    let colX = margin + 2;
    doc.setFontSize(9);
    doc.text("SR.", colX, cursorY + 5); colX += colWidths.sr;
    doc.text("TOKEN #", colX, cursorY + 5); colX += colWidths.token;
    doc.text("COUNTER", colX, cursorY + 5); colX += colWidths.counter;
    doc.text("CUSTOMER", colX, cursorY + 5); colX += colWidths.name;
    doc.text("ATTENDED", colX, cursorY + 5); colX += colWidths.attended;
    doc.text("SERVICE", colX, cursorY + 5); colX += colWidths.service;
    doc.text("STATUS", colX, cursorY + 5);
    
    cursorY += 8;
    doc.setFont(undefined, 'normal');

    // Table Rows
    this.todayTokens.forEach((t, index) => {
      // Check for page break
      if (cursorY > pageHeight - 20) {
        doc.addPage();
        cursorY = 20;
        // Re-draw header background on new page
        doc.setFont(undefined, 'bold');
        doc.setFillColor(240, 242, 245);
        doc.rect(margin, cursorY, pageWidth - (margin * 2), 8, 'F');
        let headerX = margin + 2;
        doc.text("SR.", headerX, cursorY + 5); headerX += colWidths.sr;
        doc.text("TOKEN #", headerX, cursorY + 5); headerX += colWidths.token;
        doc.text("COUNTER", headerX, cursorY + 5); headerX += colWidths.counter;
        doc.text("CUSTOMER", headerX, cursorY + 5); headerX += colWidths.name;
        doc.text("ATTENDED", headerX, cursorY + 5); headerX += colWidths.attended;
        doc.text("SERVICE", headerX, cursorY + 5); headerX += colWidths.service;
        doc.text("STATUS", headerX, cursorY + 5);
        cursorY += 8;
        doc.setFont(undefined, 'normal');
      }

      // Zebra striping
      if (index % 2 === 0) {
        doc.setFillColor(252, 252, 252);
        doc.rect(margin, cursorY, pageWidth - (margin * 2), 8, 'F');
      }

      let rowX = margin + 2;
      const attendedBy = t.token_attended && t.token_attended !== 'no' ? t.token_attended : (t.user ? t.user.name : 'Not Assigned');
      const serviceName = t.service ? t.service.name : (t.service_name || 'N/A');
      
      // FIX: Find the counter name from countersList using isTokenForCounter
      let foundCounter = this.countersList.find(c => this.isTokenForCounter(t, c));
      const counterName = t.counter_name || (t.counter ? (t.counter.name || t.counter.counter_name) : "") || (foundCounter ? foundCounter.name : "N/A");
      
      const status = (t.status || 'UNKNOWN').toUpperCase();

      doc.text((index + 1).toString(), rowX, cursorY + 5); rowX += colWidths.sr;
      doc.text(t.token_number || 'N/A', rowX, cursorY + 5); rowX += colWidths.token;
      doc.text(doc.splitTextToSize(counterName, 33)[0], rowX, cursorY + 5); rowX += colWidths.counter;
      doc.text(doc.splitTextToSize(t.token_name || t.name || 'N/A', 28)[0], rowX, cursorY + 5); rowX += colWidths.name;
      doc.text(doc.splitTextToSize(attendedBy, 28)[0], rowX, cursorY + 5); rowX += colWidths.attended;
      doc.text(doc.splitTextToSize(serviceName, 33)[0], rowX, cursorY + 5); rowX += colWidths.service;
      
      // Color code status text
      if (status === 'COMPLETED') doc.setTextColor(22, 163, 74);
      else if (status === 'PENDING' || status === 'PROCESSING' || status === 'SERVING') doc.setTextColor(202, 138, 4);
      else doc.setTextColor(220, 38, 38);
      
      doc.text(status, rowX, cursorY + 5);
      doc.setTextColor(0); // Reset to black

      cursorY += 8;
    });

    // Save the PDF
    doc.save(`Token_Data_Report_${new Date().toISOString().split('T')[0]}.pdf`);

  } catch (error) {
    console.error("PDF Export Error:", error);
    alert("Failed to export Data PDF.");
  } finally {
    btn.innerHTML = originalBtnText;
  }
},

    downloadAsExcel() {
      try {
        let csvContent = "\uFEFF"; 
        csvContent += "SR. NO.,TOKEN #,CUSTOMER NAME,ATTENDED BY,SERVICE,STATUS,DATE\n";

        this.todayTokens.forEach((t, index) => {
          const row = [
            index + 1,
            t.token_number,
            t.token_name || t.name || 'N/A',
            t.token_attended && t.token_attended !== 'no' ? t.token_attended : (t.user ? t.user.name : 'Not Assigned'),
            t.service ? t.service.name : (t.service_name || 'N/A'),
            (t.status || 'UNKNOWN').toUpperCase(),
            new Date().toLocaleDateString()
          ];
          const cleanRow = row.map(val => `"${String(val).replace(/"/g, '""')}"`);
          csvContent += cleanRow.join(",") + "\n";
        });

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", `Token_Report_${new Date().toISOString().split('T')[0]}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (error) {
        console.error("Excel Export Error:", error);
        alert("Failed to export Excel file.");
      }
    },

    selectCounterFromDropdown(counter) {
      if (!counter) {
        this.selectedCounterDrilldown = null;
        this.counterSearchQuery = '';
      } else {
        this.selectedCounterDrilldown = counter;
        this.selectedCounterForDetail = counter;
        this.counterSearchQuery = counter.name;
        this.isCounterChartExpanded = true;
      }
      this.showCounterDropdown = false;
      if (this.charts.counterStatus) {
        this.charts.counterStatus.destroy();
        this.charts.counterStatus = null;
      }
      this.$nextTick(() => {
        this.renderCounterStatusChart();
      });
    },

    handleBackDrilldown() {
      if (this.selectedCounterDrilldown && this.selectedBranchDrilldown) {
        this.selectedCounterDrilldown = null;
      } else if (this.selectedBranchDrilldown) {
        this.selectedBranchDrilldown = null;
        this.selectedCounterDrilldown = null;
        this.isCounterChartExpanded = false; 
      } else if (this.selectedCounterDrilldown && this.userRole === 'tokenmanager') {
        this.selectedCounterDrilldown = null;
        this.counterSearchQuery = '';
        this.isCounterChartExpanded = false;
      }
      if (this.charts.counterStatus) {
        this.charts.counterStatus.destroy();
        this.charts.counterStatus = null;
      }
      this.$nextTick(() => {
        setTimeout(() => {
          this.renderCounterStatusChart();
        }, 300);
      });
    },

    getBranchCounters(branch) {
      if (!branch || !this.countersList) return [];
      const bId = branch.id;
      const bName = (branch.branch_name || branch.name || '').trim().toLowerCase();

      return this.countersList.filter(c => {
        if (!c) return false;
        const cBranchId = c.branch_id || (c.branch ? c.branch.id : null);
        if (bId && cBranchId && String(cBranchId) === String(bId)) return true;
        const cBranchName = (c.branch_name || (c.branch ? (c.branch.branch_name || c.branch.name) : '') || '').trim().toLowerCase();
        if (bName && cBranchName && bName === cBranchName) return true;
        return false;
      });
    },

    getBranchAttendedTokens(branch) {
      if (!branch || !this.todayTokens || !this.todayTokens.length) return [];
      const bId = branch.id;
      const bName = (branch.branch_name || branch.name || '').trim().toLowerCase();
      const branchCounters = this.getBranchCounters(branch);

      return this.todayTokens.filter(t => {
        if (!t) return false;
        const tBranchId = t.branch_id || (t.branch ? t.branch.id : null);
        if (bId && tBranchId && String(tBranchId) === String(bId)) return true;
        const tBranchName = (t.branch_name || (t.branch ? (t.branch.branch_name || t.branch.name) : '') || '').trim().toLowerCase();
        if (bName && tBranchName && bName === tBranchName) return true;
        return branchCounters.some(c => this.isTokenForCounter(t, c));
      });
    },

    toggleExpandCounterChart() {
      this.isCounterChartExpanded = !this.isCounterChartExpanded;
      if (!this.isCounterChartExpanded) {
        this.selectedCounterDrilldown = null;
        this.selectedBranchDrilldown = null;
        this.counterSearchQuery = '';
      }
      if (this.charts.counterStatus) {
        this.charts.counterStatus.destroy();
        this.charts.counterStatus = null;
      }
      this.$nextTick(() => {
        this.renderCounterStatusChart();
      });
    },

    toggleCounterExpand(counterId) {
      const index = this.expandedCounterIds.indexOf(counterId);
      if (index > -1) {
        this.expandedCounterIds.splice(index, 1);
      } else {
        this.expandedCounterIds.push(counterId);
      }
    },

    getCounterAttendedTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => this.isTokenForCounter(t, counter));
    },

    getCounterCompletedTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => (t.status || '').toLowerCase() === 'completed' && this.isTokenForCounter(t, counter));
    },

    getCounterPendingTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => {
        const s = (t.status || '').toLowerCase();
        return (s === 'pending' || s === 'waiting' || s === 'processing' || s === 'serving') && this.isTokenForCounter(t, counter);
      });
    },

    getCounterSkippedTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => (t.status || '').toLowerCase() === 'skipped' && this.isTokenForCounter(t, counter));
    },

    getCounterRejectedTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => (t.status || '').toLowerCase() === 'rejected' && this.isTokenForCounter(t, counter));
    },

    isTokenForCounter(t, counter) {
      if (!t || !counter) return false;
      if (t.counter_id && String(t.counter_id) === String(counter.id)) return true;
      const counterName = (counter.name || "").toLowerCase().trim();
      const tokenCounterName = (t.counter_name || (t.counter ? (t.counter.name || t.counter.counter_name) : "") || "").toLowerCase().trim();
      if (counterName && tokenCounterName && counterName === tokenCounterName) return true;
      const counterUserId = counter.user_id || (counter.user ? counter.user.id : null);
      const counterAttendant = (counter.user ? counter.user.name : (counter.attendant_name || "")).toLowerCase().trim();
      const tokenUserId = t.user_id;
      const tokenAttendant = (t.token_attended || "").toLowerCase().trim();
      const matchesUser = (counterUserId && tokenUserId && String(counterUserId) === String(tokenUserId)) ||
                          (counterAttendant && tokenAttendant && counterAttendant !== 'no' && tokenAttendant !== 'no' && counterAttendant === tokenAttendant);
      if (matchesUser) return true;
      const hasExplicitCounter = t.counter_id || tokenCounterName || (tokenUserId && String(tokenUserId) !== '0') || (tokenAttendant && tokenAttendant !== 'no');
      if (hasExplicitCounter) return false;
      let counterServiceIds = [];
      if (counter.service_ids) {
        let rawIds = counter.service_ids;
        if (typeof rawIds === 'string') {
          try { rawIds = JSON.parse(rawIds); } catch (e) { rawIds = rawIds.split(',').map(id => id.trim()); }
        }
        if (Array.isArray(rawIds)) { counterServiceIds = rawIds.map(id => String(id)); } else if (rawIds) { counterServiceIds = [String(rawIds)]; }
      }
      counterServiceIds = counterServiceIds.filter(id => id && id !== 'null' && id !== 'undefined');
      const tokenServiceId = t.service_id || (t.service ? t.service.id : null);
      if (counterServiceIds.length > 0) {
        if (tokenServiceId && counterServiceIds.includes(String(tokenServiceId))) return true;
        if (t.service && t.service.prefix && t.token_number) {
          const matchedService = this.assignedServices.find(s => counterServiceIds.includes(String(s.id)));
          if (matchedService && matchedService.prefix) { return t.token_number.toLowerCase().startsWith(matchedService.prefix.toLowerCase().trim()); }
        }
      }
      return false;
    },

    getCounterAssignedTokens(counter) {
      if (!counter || !this.todayTokens || !this.todayTokens.length) return [];
      return this.todayTokens.filter(t => (t.status || '').toLowerCase() !== 'completed' && this.isTokenForCounter(t, counter));
    },

    async fetchDashboardData() {
      const headers = { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } };
      const isOperator = (this.userRole === 'operator' || this.userRole === 'opertor');
      const query = (isOperator && this.userId) ? `?user_id=${this.userId}` : '';
      try {
        const [devRes, branchRes, userRes, emergencyRes, mediaRes, roleRes, counterRes, serviceRes] = await Promise.all([
          axios.get(`${this.apiUrl}/user-device-list${query}`, headers),
          axios.get(`${this.apiUrl}/branches${query}`, headers),
          axios.get(`${this.apiUrl}/user`, headers),
          axios.get(`${this.apiUrl}/emergency/list`, headers),
          axios.get(`${this.apiUrl}/media`, headers),
          axios.get(`${this.apiUrl}/roles`, headers),
          axios.get(`${this.apiUrl}/counter`, headers),
          axios.get(`${this.apiUrl}/services/list`, headers).catch(() => ({ data: { data: [] } }))
        ]);
        if (roleRes && roleRes.data) { this.totalRoles = (Array.isArray(roleRes.data) ? roleRes.data : roleRes.data.data || []).length; }
        const assignedServiceIds = new Set();
        if (counterRes && counterRes.data) {
          let counterData = [];
          if (counterRes.data.success && Array.isArray(counterRes.data.data)) { counterData = counterRes.data.data; } else if (Array.isArray(counterRes.data)) { counterData = counterRes.data; } else if (counterRes.data.data && Array.isArray(counterRes.data.data)) { counterData = counterRes.data.data; }
          this.countersList = counterData;
          this.currentPage = 1;
          this.totalCounters = counterData.length;
          this.counterStatusStats.active = counterData.filter(c => (c.status || '').toLowerCase() === 'active').length;
          this.counterStatusStats.inactive = counterData.filter(c => (c.status || '').toLowerCase() === 'inactive').length;
          if (counterData.length > 0) { this.selectedCounterForDetail = counterData[0]; }
          this.assignedCounters = counterData.filter(c => {
            const assignedUserId = c.user_id || (c.user ? c.user.id : null);
            return String(assignedUserId) === String(this.userId);
          });
          if (this.assignedCounters.length === 0 && this.userRole === 'tokenuser') {
            const userName = (localStorage.getItem("userName") || localStorage.getItem("name") || "").toLowerCase().trim();
            if (userName) { this.assignedCounters = counterData.filter(c => {
              const attName = (c.user ? c.user.name : (c.attendant_name || "")).toLowerCase().trim();
              return attName === userName;
            }); }
          }
          const countersToProcess = (this.userRole === 'tokenuser' && this.assignedCounters.length > 0) ? this.assignedCounters : counterData;
          countersToProcess.forEach(c => {
            if (c.service_ids) {
              let rawIds = c.service_ids;
              if (typeof rawIds === 'string') { try { rawIds = JSON.parse(rawIds); } catch { rawIds = rawIds.split(',').map(id => id.trim()); } }
              if (Array.isArray(rawIds)) { rawIds.forEach(id => assignedServiceIds.add(String(id))); }
            }
          });
        }
        if (serviceRes && serviceRes.data) {
          let servicesList = [];
          if (serviceRes.data.success && Array.isArray(serviceRes.data.data)) { servicesList = serviceRes.data.data; } else if (Array.isArray(serviceRes.data.data)) { servicesList = serviceRes.data.data; } else if (Array.isArray(serviceRes.data)) { servicesList = serviceRes.data; }
          const isAllServicesRole = ['tokenmanager', 'admin', 'superadmin', 'devicemanager', 'deviceuser', 'mediamanager'].includes(this.userRole);
          if (isAllServicesRole) { this.assignedServices = servicesList; } else if (this.userRole === 'tokenuser') {
            if (assignedServiceIds.size > 0) { this.assignedServices = servicesList.filter(s => assignedServiceIds.has(String(s.id))); } else { this.assignedServices = servicesList; }
          } else { this.assignedServices = servicesList; }
          this.totalServices = this.assignedServices.length;
          this.serviceStatusStats.active = this.assignedServices.filter(s => s.status == 1 || s.status === '1' || s.status === true || (s.status || '').toLowerCase() === 'active').length;
          this.serviceStatusStats.inactive = this.totalServices - this.serviceStatusStats.active;
        }
        const devices = devRes.data.data || devRes.data || [];
        const branches = branchRes.data || [];
        this.branchesList = Array.isArray(branches) ? branches : (branches.data || []);
        this.totalDevices = devices.length;
        this.totalBranches = branches.length;
        if (isOperator) {
            this.branchStatusStats.active = branches.filter(b => b.status == 1 || b.status === '1').length;
            this.branchStatusStats.inactive = branches.filter(b => b.status == 0 || b.status === '0').length;
        }
        if (this.userRole !== 'tokenuser' && this.userRole !== 'tokenmanager') {
          try {
            const playlistRes = await axios.get(`${this.apiUrl}/playlist/list`, headers);
            const playlists = Array.isArray(playlistRes.data) ? playlistRes.data : (playlistRes.data.data || []);
            this.totalPlaylists = playlists.length;
            this.playlistStatusStats.active = playlists.filter(p => p.status == 1 || p.status === '1').length;
            this.playlistStatusStats.inactive = playlists.filter(p => p.status == 0 || p.status === '0').length;
          } catch (e) { console.error("Playlist API Error", e); }
        }
        let dist = branches.map(b => ({ name: b.branch_name, state: (b.state || 'Unknown').trim(), city: (b.city || 'Unknown').trim(), count: devices.filter(d => String(d.branch_id) === String(b.id)).length }));
        this.branchDistribution = dist.filter(item => item.count > 0);
        if (this.userRole === 'supervisor' || this.userRole === 'superviser') {
           const stateMap = {}; dist.forEach(d => { const s = d.state.charAt(0).toUpperCase() + d.state.slice(1).toLowerCase(); stateMap[s] = (stateMap[s] || 0) + d.count; });
           this.stateDeviceDist = Object.keys(stateMap).map(k => ({ name: k, count: stateMap[k] }));
           const cityMap = {}; branches.forEach(b => { const rawCity = (b.city || 'Unknown').trim(); const c = rawCity.charAt(0).toUpperCase() + rawCity.slice(1).toLowerCase(); cityMap[c] = (cityMap[c] || 0) + 1; });
           this.cityBranchDist = Object.keys(cityMap).map(k => ({ name: k, count: cityMap[k] }));
        }
        const unassigned = devices.filter(d => !d.branch_id || d.branch_id == 0).length;
        if (unassigned > 0 && this.userRole !== 'supervisor' && this.userRole !== 'superviser') { this.branchDistribution.push({ name: "No Branch", count: unassigned }); }
        if (['tokenmanager', 'deviceuser', 'admin', 'superadmin', 'tokenuser', 'operator', 'opertor'].includes(this.userRole)) {
            try {
                const tokenRes = await axios.get(`${this.apiUrl}/tokens/list?status=today`, headers);
                const resData = tokenRes.data;
                const tokens = Array.isArray(resData) ? resData : (resData.data ? (Array.isArray(resData.data) ? resData.data : (resData.data.tokens || [])) : (resData.tokens || []));
                this.todayTokens = tokens;
                this.totalTokensToday = tokens.length;
                this.tokenStatusStats.completed = tokens.filter(t => (t.status || '').toLowerCase() === 'completed').length;
                this.tokenStatusStats.pending = tokens.filter(t => { const s = (t.status || '').toLowerCase(); return s === 'pending' || s === 'waiting' || s === 'processing' || s === 'serving'; }).length;
                this.tokenStatusStats.skipped = tokens.filter(t => (t.status || '').toLowerCase() === 'skipped').length;
                this.tokenStatusStats.rejected = tokens.filter(t => (t.status || '').toLowerCase() === 'rejected').length;
            } catch(e) { console.error("Token API Error", e); }
        }
        const users = userRes.data.data || userRes.data || [];
        this.totalUsers = users.length;
        this.userStats.active = users.filter(u => u.status == 1 || u.status === '1').length;
        this.userStats.inactive = this.totalUsers - this.userStats.active;
        const emergencies = emergencyRes.data.data || [];
        this.totalEmergency = emergencies.length;
        this.emergencyStats.live = emergencies.filter(e => e.is_live).length;
        this.emergencyStats.active = emergencies.filter(e => (e.status || '').toLowerCase() === 'active' && !e.is_live).length;
        this.emergencyStats.inactive = emergencies.filter(e => (e.status || '').toLowerCase() === 'inactive').length;
        const media = mediaRes.data || [];
        this.totalMedia = media.length;
        this.mediaStatusStats = { active: { image: 0, video: 0 }, inactive: { image: 0, video: 0 } };
        media.forEach(m => { const statusKey = (m.status == 1 || m.status == '1') ? 'active' : 'inactive'; if (m.type == 1) this.mediaStatusStats[statusKey].image++; else if (m.type == 0) this.mediaStatusStats[statusKey].video++; });
        this.chartsReady = true;
        this.$nextTick(() => this.renderAllCharts());
      } catch (e) { console.error("Dashboard Load Error", e); this.chartsReady = true; }
    },

    renderAllCharts() {
      Object.values(this.charts).forEach(c => c?.destroy());
      this.renderTokenStatusBar(); this.renderCounterStatusChart(); this.renderServiceStatusChart();
      this.renderDeviceLocationDoughnut(); this.renderPlaylistStatusBarChart();
      this.renderMediaStatusBarChart(); this.renderEmergencyStatusBar(); this.renderUserStatusBarChart();
      this.renderStaffPerformanceChart(); 
    },

    renderStaffPerformanceChart() {
      if (!this.$refs.staffPerformanceChart) return;
      const performanceMap = {};
      this.todayTokens.forEach(t => {
        if (t.status === 'completed' && t.token_attended && t.token_attended !== 'No') {
          performanceMap[t.token_attended] = (performanceMap[t.token_attended] || 0) + 1;
        }
      });
      const labels = Object.keys(performanceMap);
      const data = Object.values(performanceMap);
      this.charts.staffPerformance = new Chart(this.$refs.staffPerformanceChart, {
        type: "bar",
        data: {
          labels: labels.length > 0 ? labels : ["No Attendants"],
          datasets: [{
            data: data.length > 0 ? data : [0],
            backgroundColor: labels.map(l => l.toLowerCase() === this.currentLoggedUserName.toLowerCase() ? '#2dce89' : this.themeColor + 'CC'),
            borderRadius: 6,
            barThickness: 28
          }]
        },
        options: {
          maintainAspectRatio: false,
          plugins: { 
            legend: { display: false },
            tooltip: { callbacks: { label: (ctx) => ` Completed Tokens: ${ctx.raw}` } }
          },
          scales: {
            y: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } },
            x: { ticks: { color: '#8898aa', font: { size: 10 } }, grid: { display: false } }
          }
        }
      });
    },

    renderTokenStatusBar() {
      if (!this.$refs.tokenStatusBar) return;
      this.charts.tokenStatusBar = new Chart(this.$refs.tokenStatusBar, {
        type: "bar", data: { labels: ["Completed", "Pending / Processing", "Skipped", "Rejected"], datasets: [{ data: [this.tokenStatusStats.completed, this.tokenStatusStats.pending, this.tokenStatusStats.skipped, this.tokenStatusStats.rejected], backgroundColor: ["#96f2d7", "#ffc078", this.themeColor + '99', "#ff8787"], borderRadius: 6, barThickness: 28 }], },
        options: { maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } }, x: { ticks: { color: '#8898aa', font: { size: 10 } }, grid: { display: false } } } }
      });
    },

    renderCounterStatusChart() {
      if (this.charts.counterStatus) { this.charts.counterStatus.destroy(); this.charts.counterStatus = null; }
      const canvas = this.isCounterChartExpanded ? this.$refs.counterStatusChartExpanded : this.$refs.counterStatusChart;
      if (!canvas || canvas.offsetWidth === 0) {
        setTimeout(() => {
          const retryCanvas = this.isCounterChartExpanded ? this.$refs.counterStatusChartExpanded : this.$refs.counterStatusChart;
          if (retryCanvas && retryCanvas.offsetWidth > 0) this.renderCounterStatusChart();
        }, 150);
        return;
      }
      const self = this;
      if (['tokenmanager', 'admin', 'superadmin'].includes(this.userRole)) {
        let labels = []; let dataValues = []; let itemsList = []; let isBranchLevel = false;
        let chartTitle = "Total";
        let chartSubtitle = "Counters";
        const isBranchRole = ['admin', 'superadmin'].includes(this.userRole);
        
        if (this.selectedCounterDrilldown) {
          isBranchLevel = false;
          labels = ["Completed", "Pending", "Skipped", "Rejected"];
          dataValues = [
            this.getCounterCompletedTokens(this.selectedCounterDrilldown).length,
            this.getCounterPendingTokens(this.selectedCounterDrilldown).length,
            this.getCounterSkippedTokens(this.selectedCounterDrilldown).length,
            this.getCounterRejectedTokens(this.selectedCounterDrilldown).length
          ];
          chartSubtitle = "Tokens";
        } 
        else if (isBranchRole && !this.selectedBranchDrilldown) {
          isBranchLevel = true; itemsList = this.branchesList.length > 0 ? this.branchesList : [];
          labels = itemsList.map(b => b.branch_name || b.name || 'Branch');
          dataValues = itemsList.map(b => self.getBranchAttendedTokens(b).length);
          chartSubtitle = "Branches";
        } 
        else if (this.selectedBranchDrilldown && isBranchRole) {
          isBranchLevel = false; itemsList = this.getBranchCounters(this.selectedBranchDrilldown);
          if (itemsList.length === 0) { itemsList = [{ id: 0, name: 'No Counters' }]; }
          labels = itemsList.map(c => c.name);
          dataValues = itemsList.map(c => self.getCounterAttendedTokens(c).length);
          chartSubtitle = "Counters";
        } else {
          isBranchLevel = false; itemsList = this.countersList.length > 0 ? this.countersList : [{ id: 0, name: 'Unassigned' }];
          labels = itemsList.map(c => c.name);
          dataValues = itemsList.map(c => self.getCounterAttendedTokens(c).length);
          chartSubtitle = "Counters";
        }
        const totalSum = dataValues.reduce((a, b) => a + b, 0);
        const statusColors = ["#2dce89", "#ffc078", "#adb5bd", "#f5365c"];
        const vibrantColors = ["#a3a09b", "#8cf0aa", "#e5a800", "#0d9488", "#a855f7", "#0ea5e9", "#ef4444", "#06b6d4", "#22c55e", "#f59e0b"];
        const colors = this.selectedCounterDrilldown ? statusColors : vibrantColors.slice(0, Math.max(labels.length, 1));
        this.charts.counterStatus = new Chart(canvas, {
          type: "doughnut",
          data: { labels: labels, datasets: [{ data: dataValues, backgroundColor: colors, borderWidth: 2, borderColor: '#ffffff' }], },
          options: {
            maintainAspectRatio: false, cutout: '66%',
            plugins: {
              legend: {
                position: 'right',
                labels: {
                  boxWidth: 8, usePointStyle: true, pointStyle: 'circle', font: { size: 10 }, padding: 8,
                  generateLabels: (chart) => {
                    const data = chart.data;
                    return data.labels.map((label, i) => {
                      const count = data.datasets[0].data[i] || 0;
                      let labelText = label + " (" + count + ")";
                      return { text: labelText, fillStyle: data.datasets[0].backgroundColor[i], strokeStyle: '#fff', lineWidth: 0, hidden: false, index: i };
                    });
                  }
                },
                onClick: (e, legendItem) => {
                  if (this.selectedCounterDrilldown) return;
                  const selected = itemsList[legendItem.index];
                  if (selected) {
                    if (isBranchLevel) {
                      self.selectedBranchDrilldown = selected;
                      const bc = self.getBranchCounters(selected);
                      if (bc.length > 0) { self.selectedCounterDrilldown = bc[0]; }
                      self.isCounterChartExpanded = true;
                    } else { 
                      self.selectedCounterDrilldown = selected; 
                      self.isCounterChartExpanded = true; 
                    }
                    self.$nextTick(() => { self.renderCounterStatusChart(); });
                  }
                }
              },
              tooltip: { callbacks: { label: (context) => ` ${context.label}: ${context.raw}` } }
            },
            onClick: (e, elements) => {
              if (this.selectedCounterDrilldown) return;
              if (elements && elements.length > 0) {
                const idx = elements[0].index; const selected = itemsList[idx];
                if (selected) {
                  if (isBranchLevel) {
                    self.selectedBranchDrilldown = selected;
                    const bc = self.getBranchCounters(selected);
                    if (bc.length > 0) { self.selectedCounterDrilldown = bc[0]; }
                    self.isCounterChartExpanded = true;
                  } else { 
                    self.selectedCounterDrilldown = selected; 
                    self.isCounterChartExpanded = true; 
                  }
                  self.$nextTick(() => { self.renderCounterStatusChart(); });
                }
              }
            }
          },
          plugins: [{
            id: 'counterCenterTextDrilldown',
            beforeDraw(chart) {
              const { ctx } = chart; ctx.restore(); const chartArea = chart.chartArea; 
              const centerX = (chartArea.left + chartArea.right) / 2; 
              const centerY = (chartArea.top + chartArea.bottom) / 2;
              ctx.font = `500 0.75em sans-serif`; ctx.fillStyle = "#64748b"; ctx.textBaseline = "middle"; ctx.textAlign = "center"; 
              ctx.fillText(chartTitle, centerX, centerY - 10);
              ctx.font = `bold 1.1em sans-serif`; ctx.fillStyle = "#1e293b"; 
              ctx.fillText(totalSum.toString(), centerX, centerY + 8);
              ctx.font = `600 0.6em sans-serif`; ctx.fillStyle = "#8898aa";
              ctx.fillText(chartSubtitle.toUpperCase(), centerX, centerY + 22);
              ctx.save();
            }
          }]
        }); return;
      }
      this.charts.counterStatus = new Chart(canvas, {
        type: "doughnut", data: { labels: ["Active", "Inactive"], datasets: [{ data: [this.counterStatusStats.active, this.counterStatusStats.inactive], backgroundColor: ["#2dce89", "#adb5bd"], borderWidth: 2, borderColor: '#fff' }], },
        options: { maintainAspectRatio: false, cutout: '75%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 }, padding: 15 } } } },
        plugins: [{
          id: 'counterCenterText',
          beforeDraw(chart) {
            const { ctx, width, height } = chart; ctx.restore(); ctx.font = `bold 1.2em sans-serif`; ctx.textBaseline = "middle"; ctx.fillStyle = "#32325d";
            const text = self.totalCounters.toString(); const textX = Math.round((width - ctx.measureText(text).width) / 2); const textY = height / 2 - 10; ctx.fillText(text, textX, textY);
            ctx.font = `600 0.7em sans-serif`; ctx.fillStyle = "#8898aa"; const label = "COUNTERS"; const labelX = Math.round((width - ctx.measureText(label).width) / 2); const labelY = height / 2 + 15; ctx.fillText(label, labelX, labelY); ctx.save();
          }
        }]
      });
    },

    renderServiceStatusChart() {
      if (!this.$refs.serviceStatusChart) return;
      const labels = this.assignedServices.map(s => s.name);
      const data = this.assignedServices.map(s => { return this.todayTokens.filter(t => String(t.service_id) === String(s.id) || (t.service && String(t.service.id) === String(s.id)) || (t.service && t.service.name === s.name)).length; });
      this.charts.serviceStatus = new Chart(this.$refs.serviceStatusChart, {
        type: "bar", data: { labels: labels.length > 0 ? labels : ["No Services Available"], datasets: [{ label: "Tokens Today", data: labels.length > 0 ? data : [0], backgroundColor: labels.map(() => this.themeColor + 'CC'), borderRadius: 6, barThickness: labels.length > 5 ? 20 : 35 }], },
        options: { maintainAspectRatio: false, plugins: { legend: { display: false }, tooltip: { callbacks: { label: (context) => ` Tokens Generated Today: ${context.raw}` } } }, scales: { y: { beginAtZero: true, suggestedMax: 5, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } }, x: { ticks: { color: '#8898aa', font: { weight: 'bold', size: 10 } }, grid: { display: false } } } }
      });
    },

    renderDeviceLocationDoughnut() {
      if (!this.$refs.deviceLocationDoughnut) return;
      const isSupervisor = (this.userRole === 'supervisor' || this.userRole === 'superviser');
      const dataSrc = isSupervisor ? this.stateDeviceDist : this.branchDistribution;
      this.charts.deviceDoughnut = new Chart(this.$refs.deviceLocationDoughnut, {
        type: isSupervisor ? "pie" : "doughnut", data: { labels: dataSrc.map(b => b.name), datasets: [{ data: dataSrc.map(b => b.count), backgroundColor: ["#1e3a5f", "#0ea5e9", "#6366f1", "#8b5cf6", "#ec4899", "#f43f5e", "#f97316", "#eab308", "#22c55e"], borderWidth: 2, borderColor: '#fff' }], },
        options: { maintainAspectRatio: false, cutout: isSupervisor ? 0 : '75%', plugins: { legend: { position: 'bottom', labels: { boxWidth: 10, font: { size: 10 }, padding: 15 } } } },
        plugins: isSupervisor ? [] : [{
          id: 'centerText',
          beforeDraw(chart) {
            const { ctx, width, height } = chart; ctx.restore(); const fontSizeNum = (height / 110).toFixed(2); ctx.font = `bold ${fontSizeNum}em sans-serif`; ctx.textBaseline = "middle"; ctx.fillStyle = "#32325d";
            const text = chart.data.datasets[0].data.reduce((a, b) => a + b, 0).toString(); const textX = Math.round((width - ctx.measureText(text).width) / 2); const textY = height / 2 - 15; ctx.fillText(text, textX, textY);
            const fontSizeLabel = (height / 350).toFixed(2); ctx.font = `600 ${fontSizeLabel}em sans-serif`; ctx.fillStyle = "#8898aa"; const label = "DEVICES"; const labelX = Math.round((width - ctx.measureText(label).width) / 2); const labelY = height / 2 + 15; ctx.fillText(label, labelX, labelY); ctx.save();
          }
        }]
      });
    },

    renderPlaylistStatusBarChart() {
      if (!this.$refs.playlistStatusBarChart) return;
      const isSupervisor = (this.userRole === 'supervisor' || this.userRole === 'superviser');
      const isOperator = (this.userRole === 'operator' || this.userRole === 'opertor');
      let labels, data, colors;
      if (isSupervisor) { labels = this.cityBranchDist.map(c => c.name); data = this.cityBranchDist.map(c => c.count); colors = this.themeColor + 'CC'; } else if (isOperator) { labels = ["Active Branches", "Inactive Branches"]; data = [this.branchStatusStats.active, this.branchStatusStats.inactive]; colors = ["#2dce89", "#adb5bd"]; } else { labels = ["Active", "Inactive"]; data = [this.playlistStatusStats.active, this.playlistStatusStats.inactive]; colors = [this.themeColor + 'CC', "#dee2e6"]; }
      this.charts.playlistBar = new Chart(this.$refs.playlistStatusBarChart, {
        type: "bar", data: { labels: labels, datasets: [{ data: data, backgroundColor: colors, borderRadius: 6, barThickness: (isSupervisor || isOperator) ? 30 : 50 }], },
        options: { maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } }, x: { ticks: { color: '#8898aa', font: { weight: 'bold', size: 9 } }, grid: { display: false } } } }
      });
    },

    renderMediaStatusBarChart() {
      if (!this.$refs.mediaStatusBarChart) return;
      const isSupervisor = (this.userRole === 'supervisor' || this.userRole === 'superviser');
      if (isSupervisor) {
        this.charts.mediaBar = new Chart(this.$refs.mediaStatusBarChart, { type: "bar", data: { labels: this.branchDistribution.map(b => b.name), datasets: [{ label: "Devices", data: this.branchDistribution.map(b => b.count), backgroundColor: this.themeColor + 'CC', borderRadius: 4, barThickness: 30 }] }, options: { maintainAspectRatio: false, plugins: { legend: { display: false } }, scales: { y: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } }, x: { ticks: { color: '#8898aa', font: { weight: 'bold', size: 9 } }, grid: { display: false } } } } }); return;
      }
      this.charts.mediaBar = new Chart(this.$refs.mediaStatusBarChart, {
        type: "bar", data: { labels: ["Active", "Inactive"], datasets: [{ label: "Active Images", data: [this.mediaStatusStats.active.image, 0], backgroundColor: this.themeColor + 'CC', borderRadius: 4, barThickness: 50 }, { label: "Active Videos", data: [this.mediaStatusStats.active.video, 0], backgroundColor: "#74c0fc", borderRadius: 4, barThickness: 50 }, { label: "Inactive Images", data: [0, this.mediaStatusStats.inactive.image], backgroundColor: "#adb5bd", borderRadius: 4, barThickness: 50 }, { label: "Inactive Videos", data: [0, this.mediaStatusStats.inactive.video], backgroundColor: "#dee2e6", borderRadius: 4, barThickness: 50 } ], },
        options: { maintainAspectRatio: false, plugins: { legend: { display: true, position: 'bottom', labels: { boxWidth: 8, font: { size: 9 }, padding: 10 } } }, scales: { y: { stacked: true, beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { borderDash: [3], drawBorder: false, color: '#f1f3f5' } }, x: { stacked: true, ticks: { color: '#8898aa', font: { weight: 'bold' } }, grid: { display: false } } } }
      });
    },

    renderEmergencyStatusBar() {
      if (!this.$refs.emergencyStatusBar) return;
      this.charts.emergencyStatusBar = new Chart(this.$refs.emergencyStatusBar, {
        type: "bar", data: { labels: ["Live Broadcasts", "Active Templates", "Inactive Templates"], datasets: [{ data: [this.emergencyStats.live, this.emergencyStats.active, this.emergencyStats.inactive], backgroundColor: ["#a03636", this.themeColor + '99', "#dee2e6"], borderRadius: 8, barThickness: 45 }], },
        options: { maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { display: false } }, y: { ticks: { color: '#495057', font: { weight: 'bold' } }, grid: { display: false } } } }
      });
    },

    renderUserStatusBarChart() {
      if (!this.$refs.userStatusBarChart) return;
      this.charts.userStatusBar = new Chart(this.$refs.userStatusBarChart, {
        type: "bar", data: { labels: ["Active Users", "Inactive Users"], datasets: [{ data: [this.userStats.active, this.userStats.inactive], backgroundColor: [this.themeColor + 'CC', "#f1f3f5"], borderRadius: 8, barThickness: 45 }], },
        options: { maintainAspectRatio: false, indexAxis: 'y', plugins: { legend: { display: false } }, scales: { x: { beginAtZero: true, ticks: { stepSize: 1, color: '#adb5bd' }, grid: { display: false } }, y: { ticks: { color: '#495057', font: { weight: 'bold' } }, grid: { display: false } } } }
      });
    }
  },

  mounted() { 
    this.themeColor = localStorage.getItem('themeColor') || '#1e3a5f';
    this.fetchDashboardData(); 
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() { 
    Object.values(this.charts).forEach(c => c?.destroy());
    document.removeEventListener('click', this.handleDocumentClick);
  }
};
</script>

<style scoped>
.kpi-card { background: #fff; border-radius: 1rem; padding: 1.2rem; box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.04); height: 100%; border: 1px solid #f8f9fa; display: flex; flex-direction: column; }
.kpi-header { display: flex; align-items: center; gap: 0.8rem; margin-bottom: 0.8rem; }
.kpi-icon { width: 38px; height: 38px; border-radius: 10px; display: flex; align-items: center; justify-content: center; font-size: 0.9rem; }
.bg-soft-info { background-color: #e7f5ff; }
.bg-soft-success { background-color: #e6fffa; }
.bg-soft-danger { background-color: #fff5f5; }
.bg-soft-secondary { background-color: #f8f9fa; }
.bg-soft-primary { background-color: #e0e7ff; }
.kpi-label { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; color: #adb5bd; margin: 0; letter-spacing: 0.02em; }
.kpi-value { font-size: 1.1rem; font-weight: 700; color: #495057; margin: 0; }
.chart-box-pie { position: relative; height: 250px; width: 100%; flex-grow: 1; }
.chart-box-bar-wide { position: relative; height: 180px; width: 100%; }
.italic { font-style: italic; }
.circle-btn { width: 26px; height: 26px; border-radius: 50%; border: 1px solid #cbd5e1; background-color: #ffffff; display: inline-flex; align-items: center; justify-content: center; font-size: 0.7rem; color: #475569; cursor: pointer; transition: all 0.15s ease-in-out; padding: 0; }
.circle-btn:hover { background-color: #f1f5f9; border-color: #94a3b8; }
.dropdown-counter-item:hover { background-color: #f1f5f9 !important; }
</style>